import type { GameTrackingInfo } from '@/types/games';
import { buildGameTileMap, getGameZoneTileBoundingRectForOnScreen } from '@/utils/tracking/game-zone-tile-visibility';

export const SEARCH_RESULTS_GRID_SELECTOR = '.found-games_container';

const EDGE_TOLERANCE_PX = 1;

export type BoundsRect = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

export type VisibleSearchResultGame<T extends GameTrackingInfo = GameTrackingInfo> = {
    game: T;
    position: number;
};

/**
 * Keyboard-aware viewport. The on-screen keyboard is open for the whole duration of a drawer
 * search and shrinks `visualViewport` without changing `window.innerHeight`, so measuring against
 * `innerHeight` would count tiles hidden behind the keyboard as visible.
 */
export const getVisualViewportRect = (): BoundsRect => {
    if (typeof window === 'undefined') {
        return { top: 0, left: 0, right: 0, bottom: 0 };
    }
    const vv = window.visualViewport;
    if (!vv) {
        return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
    }
    return {
        top: vv.offsetTop,
        left: vv.offsetLeft,
        right: vv.offsetLeft + vv.width,
        bottom: vv.offsetTop + vv.height
    };
};

export const intersectRects = (a: BoundsRect, b: BoundsRect): BoundsRect | null => {
    const top = Math.max(a.top, b.top);
    const left = Math.max(a.left, b.left);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) {
        return null;
    }
    return { top, left, right, bottom };
};

/**
 * Both bounds are required: the drawer is a fixed-height clipped panel, so a tile scrolled past its
 * bottom edge is still inside the viewport, and a tile inside the drawer can still sit behind the
 * keyboard. Returns `null` when the drawer is entirely off the visible viewport.
 */
export const getSearchDrawerVisibleArea = (scrollContainer: HTMLElement): BoundsRect | null => {
    const scrollRect = scrollContainer.getBoundingClientRect();
    const scrollBounds: BoundsRect = {
        top: scrollRect.top,
        left: scrollRect.left,
        right: scrollRect.right,
        bottom: scrollRect.bottom
    };
    return intersectRects(scrollBounds, getVisualViewportRect());
};

const rectIntersectsBounds = (rect: DOMRectReadOnly, area: BoundsRect): boolean => {
    if (rect.width <= 0 || rect.height <= 0) {
        return false;
    }
    const t = EDGE_TOLERANCE_PX;
    return (
        rect.right > area.left - t &&
        rect.left < area.right + t &&
        rect.bottom > area.top - t &&
        rect.top < area.bottom + t
    );
};

/**
 * Measures the thumbnail via the shared game-zone helper so `on_screen` means the same thing for
 * search results as it does for game-zone impressions.
 */
export const isSearchResultTileVisible = (tile: HTMLElement, visibleArea: BoundsRect): boolean => {
    const rect = getGameZoneTileBoundingRectForOnScreen(tile);
    return rectIntersectsBounds(rect, visibleArea);
};

/**
 * Returns search-result games whose thumbnail intersects the drawer scroll area and the visual
 * viewport (keyboard-aware). Positions are 1-based ranks in the full result list.
 */
export const getVisibleSearchResultGames = <T extends GameTrackingInfo>(
    scrollContainer: HTMLElement | null,
    games: T[]
): VisibleSearchResultGame<T>[] => {
    if (!scrollContainer || games.length === 0) {
        return [];
    }

    const visibleArea = getSearchDrawerVisibleArea(scrollContainer);
    if (!visibleArea) {
        return [];
    }

    const tileMap = buildGameTileMap(scrollContainer, games, SEARCH_RESULTS_GRID_SELECTOR);
    const visible: VisibleSearchResultGame<T>[] = [];

    games.forEach((game, index) => {
        const tile = tileMap.get(game.uid);
        if (tile && isSearchResultTileVisible(tile, visibleArea)) {
            visible.push({ game, position: index + 1 });
        }
    });

    return visible;
};
